import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';

import Model from '@tripian/model';
import { FormTemplateTrip2 as FormTemplateTrip, PreLoading, Notification } from '@tripian/react';

import './CreateUpdateTrip.scss';
import config from '../../config';
import { UPDATE_TRIP, HOME, USE_TRIP_URL } from '../../constants/ROUTER_PATH_TITLE';

import useTripForm from '../../hooks/useTripForm';
import useCompanion from '../../hooks/useCompanion';
import useTrip from '../../hooks/useTrip';
import useUser from '../../hooks/useUser';
import AppNav from '../../App/AppNav/AppNav';

const UpdateTripPage = () => {
  const { loadingCities, loadingQuestionsTrip, loadingQuestionsCompanion, cities, questionsTrip, questionsCompanion } = useTripForm();
  const [showWarningMessage, setShowWarningMessage] = useState<number>(0);

  const [tripProfileFormData, setTripProfileFormData] = useState<Model.TripProfile | undefined>(undefined);
  const [initialState, setInitialState] = useState<Model.TripProfile | undefined>(undefined);

  const { tripReferences } = useUser();
  const { companions, companionAdd, loadingCompanions } = useCompanion();
  const { tripReference, tripUpdate, loadingTripUpdate, tripFetchCallback, tripReferenceFetch } = useTrip();

  const { hash } = useParams<{ hash: string }>();
  const history = useHistory();

  if (hash) {
    document.title = `${UPDATE_TRIP.TITLE}`;
  } else {
    history.push(HOME.PATH);
  }

  useEffect(() => {
    if (config.HIDE_UPDATE_TRIP) {
      history.replace(USE_TRIP_URL.PATH);
    }
  }, [history]);

  useEffect(() => {
    let unmonted = false;

    if (tripReference) {
      // from: TripPage or TravelGuide

      if (hash !== tripReference.trip_hash) {
        // Olmaması gereken bir durum. Bir süre kalsın dedik.
        console.error('redux da tripReference.trip_hash var, url den farklı! Nasıl olur!: ', tripReference, tripReference.trip_hash, hash); // eslint-disable-line no-console
        history.push(HOME.PATH);
      }

      setTripProfileFormData(tripReference.trip_profile);
      setInitialState(tripReference.trip_profile);
    } else {
      const willUpdateTripReference = tripReferences?.find((tr) => tr.trip_hash === hash);

      if (willUpdateTripReference) {
        // from: HomePage
        setTripProfileFormData(willUpdateTripReference.trip_profile);
        setInitialState(willUpdateTripReference.trip_profile);
      } else {
        // from: Directly Url
        tripReferenceFetch(hash).then((responseTripReference: Model.TripReference) => {
          if (!unmonted) {
            setTripProfileFormData(responseTripReference.trip_profile);
            setInitialState(responseTripReference.trip_profile);
          }
        });
      }
    }

    return () => {
      unmonted = true;
    };
  }, [hash, tripReferences, tripReference, tripReferenceFetch, history]);

  const checkWarning = (tripProfileParam: Model.TripProfile) => {
    if (showWarningMessage === 0) {
      if (tripProfileParam.companion_ids.length > tripProfileParam.number_of_adults + (tripProfileParam.number_of_children || 0) - 1) {
        setShowWarningMessage(1);
      }
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const areDiffObject = (prevObject: any, currentObject: any) => {
    const prevObjectString = JSON.stringify(prevObject);
    const currentObjectString = JSON.stringify(currentObject);

    if (prevObjectString !== currentObjectString) {
      return true;
    }

    return false;
  };

  const callbackTripProfile = (tripProfileParam: Model.TripProfile) => {
    setTripProfileFormData(tripProfileParam);
    checkWarning(tripProfileParam);
  };

  const tripCancel = () => {
    history.goBack();
  };

  const onTripUpdate = (doNotGenerate = 0) => {
    if (tripProfileFormData) {
      const newTripProfileFormData = { ...tripProfileFormData };
      newTripProfileFormData.do_not_generate = doNotGenerate;

      tripUpdate(hash, newTripProfileFormData).then((trip: Model.Trip) => {
        tripFetchCallback(trip);
        history.goBack();
      });
    }
  };

  const updateButtonClick = () => {
    if (initialState && tripProfileFormData) {
      const isAccommendationValueChanged = areDiffObject(initialState.accommodation || {}, tripProfileFormData.accommodation || {});
      const isDefaultStateChanged = areDiffObject(
        {
          ...initialState,
          accommodation: null,
          answers: [...initialState.answers].sort((a, b) => a - b),
          companion_ids: [...initialState.companion_ids].sort((a, b) => a - b),
        },
        {
          ...tripProfileFormData,
          accommodation: null,
          answers: [...tripProfileFormData.answers].sort((a, b) => a - b),
          companion_ids: [...tripProfileFormData.companion_ids].sort((a, b) => a - b),
        },
      );

      if (!isDefaultStateChanged && isAccommendationValueChanged) {
        onTripUpdate(1);
      } else if (isDefaultStateChanged) {
        onTripUpdate();
      } else {
        onTripUpdate();
      }
    }
  };

  // const isButtonDisabled = !tripProfileFormData || moment(tripProfileFormData.arrival_datetime).utcOffset(0) < moment().utcOffset(0);

  // Renders
  if (loadingCities || loadingQuestionsTrip || loadingQuestionsCompanion || loadingCompanions)
    return (
      <>
        <AppNav header={UPDATE_TRIP.HEADER} />
        <PreLoading bgColor="rgba(238, 238, 238, 0.8)" />
      </>
    );

  return (
    <>
      <AppNav header={UPDATE_TRIP.HEADER} />
      <div className="container py12">
        {loadingTripUpdate ? (
          <div className="create-update-trip-loading">
            <PreLoading bgColor="rgba(238, 238, 238, 0.8)" />
          </div>
        ) : null}
        {tripProfileFormData ? (
          <>
            <FormTemplateTrip
              cities={cities || []}
              tripProfile={tripProfileFormData}
              tripQuestions={questionsTrip || []}
              callbackTripProfile={callbackTripProfile}
              userCompanionQuestions={questionsCompanion || []}
              companionLoadingList={
                []
                /* companionLoadingList */
              }
              callbackUserCompanionAdd={(companion) => {
                /** TODO Companion callback model diff
                 * @Ahmet
                 */
                companionAdd(companion as Model.CompanionRequest);
              }}
              userCompanions={companions || []}
              isTripEdit
              onSubmitText="Update My Trip"
              onSubmit={updateButtonClick}
              onCancel={tripCancel}
            />
            {/* <div className="row center">
              <div>
                <Button color={isButtonDisabled ? 'disabled' : 'primary'} style={{ marginRight: '1rem' }} text="Update My Trip" disabled={isButtonDisabled} onClick={updateButtonClick} />
                <Button color="primary" text="Cancel" onClick={tripCancel} />
              </div>
            </div> */}
            {showWarningMessage === 1 ? (
              <Notification
                type="warning"
                positionX="center"
                positionY="top"
                title="Travelers count warning"
                message="Number of travelers and travel companion(s) previously added to this trip don't match. Got it."
                onClose={() => {
                  setShowWarningMessage(2);
                }}
                closeMs={3500}
              />
            ) : null}
          </>
        ) : null}
      </div>
    </>
  );
};

export default UpdateTripPage;
